/**
 * SceneCanvas - Simple canvas for visual sprite editing
 */

import React, { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import Moveable from 'react-moveable';
import { useSceneStore } from '../../../stores/sceneStore';
import { useApiClient } from '../../../api/client';
import type { Orientation } from '../../../../../Shared/src/@types';
import { computeRevealTime } from '../../../../../Shared/src/utils/sceneTiming';
import { evaluateFrame } from '../../../../../Shared/src/runtime/evaluateFrame';
import { backgroundChangeStyle } from '../../../utils/backgroundChangeStyle';
import type { FrameState, FrameSprite, FrameDialog } from '../../../../../Shared/src/@types';
import { DIALOG_FONT, createDialogLayoutCache, dialogBoxText, visibleDialogLines, type DialogBoxLayout, type DialogBoxRect } from '../../../../../Shared/src/runtime/dialogBox';
import { choicePanelBottom } from '../../../../../Shared/src/runtime/playerStage';
import { registerProbe } from '../../../utils/conformanceHooks';
import CanvasChoicePanel, { focusChoiceInput, useChoiceEditorFocused } from '../choices/CanvasChoicePanel';
import { previewDurationMs } from '../../../utils/sceneDuration';
import type { ChoiceOption } from '../../../../../Shared/src/@types';
import { clampSpritePosition, isPoseOffCanvas, stageSize } from '../../../utils/spriteGeometry';
import importedStyles from './SceneCanvas.module.scss';
import { useMediaSheetStore } from '../../../stores/mediaSheetStore';
import { sheetFrameRect } from '../../../../../Shared/src/utils/spriteSheet';
import { containRect } from '../../../../../Shared/src/utils/spriteSize';
import type { SheetCut } from '../../../../../Shared/src/@types';

const styles = importedStyles || {} as Record<string, string>;

/** Measures a string's width in stage px in `DIALOG_FONT`, as the player's `ctx.measureText` does. */
export type DialogMeasurer = (s: string) => number;

let sharedMeasurer: DialogMeasurer | null = null;
/**
 * The editing canvas's dialog measurer (spec 017): the 2D context of an
 * offscreen canvas set to `DIALOG_FONT`. Where no 2D context exists (jsdom
 * without canvas) it falls back to an estimate of 9 px per character.
 */
export function canvasDialogMeasurer(): DialogMeasurer {
  if (sharedMeasurer) return sharedMeasurer;
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    // jsdom has no 2D context (and logs when asked for one)
    const jsdom = typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent);
    ctx = typeof document !== 'undefined' && !jsdom ? document.createElement('canvas').getContext('2d') : null;
  } catch {
    ctx = null;
  }
  if (ctx && typeof ctx.measureText === 'function') {
    const c = ctx;
    c.font = DIALOG_FONT;
    sharedMeasurer = (s: string) => {
      if (c.font !== DIALOG_FONT) c.font = DIALOG_FONT;
      return c.measureText(s).width;
    };
  } else {
    sharedMeasurer = (s: string) => Array.from(s).length * 9;
  }
  return sharedMeasurer;
}

/** Space kept around the stage when it is fitted into the workspace (each side). */
const FIT_PADDING = 24;

/** The fitted zoom for a stage in a workspace: whole stage visible with padding, never above 100 %. */
export function fitZoom(workspace: { width: number; height: number }, stage: { width: number; height: number }): number {
  const availW = workspace.width - FIT_PADDING * 2;
  const availH = workspace.height - FIT_PADDING * 2;
  const scale = Math.min(availW / stage.width, availH / stage.height, 1);
  return Math.round(scale * 100) / 100;
}

/** A Moveable gesture's running values (the sprite's frame), applied to the DOM until release. */
/** D-3 (spec 017): how a sprite invisible at the playhead is drawn on the parked canvas. */
const GHOST_OPACITY = 0.25;
const GHOST_OUTLINE = '1px dashed rgba(255, 255, 255, 0.8)';

interface GestureFrame {
  scale: number;
  rotation: number;
  hingeX: number;
  hingeY: number;
}

/**
 * A spritesheet frame on the editing canvas (spec 033): the sheet inside a
 * clipping box the size of the frame's contain-fit in the sprite box, offset
 * and scaled so the frame's rectangle fills it. Until the image's natural
 * size is known the sheet loads hidden (never the whole sheet).
 */
function SheetFrameImage({ src, alt, mediaId, cut, index, pixelArt, box, natural, flip, onLoad }: {
  src: string;
  alt: string;
  mediaId: number;
  cut: SheetCut;
  index: number;
  pixelArt: boolean;
  box: { width: number; height: number };
  natural: { width: number; height: number } | undefined;
  /** Spec 038: the mirror `flipCss` gives, around the frame's centre (the box centre: the fit is centred). */
  flip?: string;
  onLoad: (img: HTMLImageElement) => void;
}) {
  const rect = natural ? sheetFrameRect(cut, index, natural) : null;
  const ready = !!(natural && rect && rect.w > 0 && rect.h > 0);
  const fit = ready ? containRect(box, { width: rect!.w, height: rect!.h }) : { x: 0, y: 0, width: box.width, height: box.height };
  const k = ready ? fit.width / rect!.w : 1;
  return (
    <div
      className={styles.sheetFrame}
      data-testid="sheet-frame"
      data-sheet-index={index}
      style={{ position: 'absolute', left: fit.x, top: fit.y, width: fit.width, height: fit.height, overflow: 'hidden', pointerEvents: 'none', ...(flip ? { transform: flip } : {}) }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        data-media-id={mediaId}
        onLoad={(e) => onLoad(e.currentTarget)}
        style={{
          position: 'absolute',
          left: ready ? -rect!.x * k : 0,
          top: ready ? -rect!.y * k : 0,
          width: ready ? natural!.width * k : undefined,
          height: ready ? natural!.height * k : undefined,
          maxWidth: 'none',
          maxHeight: 'none',
          visibility: ready ? undefined : 'hidden',
          imageRendering: pixelArt ? 'pixelated' : undefined,
        }}
      />
    </div>
  );
}

/**
 * Spec 038 D-1: a flip mirrors the sprite's picture inside its box, around
 * the box centre; the sprite element, Moveable and hit testing are unchanged.
 * Undefined when unflipped (no transform added).
 */
const flipCss = (flipX: boolean, flipY: boolean): string | undefined =>
  (flipX || flipY ? [flipX ? 'scaleX(-1)' : '', flipY ? 'scaleY(-1)' : ''].filter(Boolean).join(' ') : undefined);

const spriteTransformCss = (f: GestureFrame) =>
  `translate(${-f.hingeX * 100}%, ${-f.hingeY * 100}%) rotate(${f.rotation}deg) scale(${f.scale})`;

export interface SceneCanvasProps {
  /** Measures dialog text (stage px, `DIALOG_FONT`); tests inject a fake. Default: `canvasDialogMeasurer()`. */
  measureDialogText?: DialogMeasurer;
}

export const SceneCanvas: React.FC<SceneCanvasProps> = ({ measureDialogText }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [spriteImages, setSpriteImages] = useState<Record<number, string>>({});
  const [backgroundImages, setBackgroundImages] = useState<Record<string, string>>({});
  // Natural image sizes by media id: a looping background translate wraps by them (evaluateFrame)
  const [imageSizes, setImageSizes] = useState<Record<number, { width: number; height: number }>>({});
  const noteImageSize = useCallback((mediaId: number, img: HTMLImageElement) => {
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (!(width > 0 && height > 0)) return;
    setImageSizes((prev) => (prev[mediaId]?.width === width && prev[mediaId]?.height === height
      ? prev
      : { ...prev, [mediaId]: { width, height } }));
  }, []);
  const apiClient = useApiClient();

  // Store state - scene now directly contains sprites, dialogs, etc.
  const scene = useSceneStore((state) => state.scene);
  const selection = useSceneStore((state) => state.selection);
  const previewOrientation = useSceneStore((state) => state.previewOrientation);
  const isPlaying = useSceneStore((state) => state.isPlaying);
  const playheadPosition = useSceneStore((state) => state.playheadPosition);
  const viewportZoom = useSceneStore((state) => state.viewportZoom);
  const viewportPan = useSceneStore((state) => state.viewportPan);
  const authoredKeyframes = useSceneStore((state) => state.keyframes);
  const choiceEditorFocused = useChoiceEditorFocused();
  
  // Store actions
  const selectSingle = useSceneStore((state) => state.selectSingle);
  // One commit for every gesture (spec 019): auto-keys an animated property
  const commitSpriteTransform = useSceneStore((state) => state.commitSpriteTransform);
  const sceneId = scene?.id;
  const setViewportZoom = useSceneStore((state) => state.setViewportZoom);
  const setViewportPan = useSceneStore((state) => state.setViewportPan);

  // The stage at the active orientation (canvas px)
  const dimensions = useMemo(() => stageSize(previewOrientation), [previewOrientation]);

  // What the canvas shows at the playhead (spec 017): spec 012's evaluateFrame
  // on the playable scene, which plays the authored keyframes (spec 006).
  // The spritesheet cuts of the pictures (spec 033), loaded with the images
  const sheets = useMediaSheetStore((state) => state.sheets);
  const playable = useMemo(
    () => (scene ? useSceneStore.getState().getPlayableScene() : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scene, authoredKeyframes, sheets],
  );
  const frame: FrameState | null = useMemo(() => {
    if (!playable) return null;
    return evaluateFrame(playable, playheadPosition, previewOrientation, { imageSizes });
  }, [playable, playheadPosition, previewOrientation, imageSizes]);
  const frameRef = useRef(frame);
  frameRef.current = frame;
  // The conformance hook (dev and test builds only)
  useEffect(() => registerProbe('canvas', { getFrameState: () => frameRef.current }), []);

  // Fit the stage into the workspace (24 px padding, never above 100 %)
  const fitToView = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || wrapper.clientWidth <= 0 || wrapper.clientHeight <= 0) return;
    const stage = stageSize(useSceneStore.getState().previewOrientation);
    setViewportZoom(fitZoom({ width: wrapper.clientWidth, height: wrapper.clientHeight }, stage));
    setViewportPan({ x: 0, y: 0 });
  }, [setViewportZoom, setViewportPan]);

  // Automatic fit (spec 005): on opening a scene without a saved viewport, on
  // an orientation switch, and when the workspace changes size. A restored
  // viewport is otherwise kept.
  const fitKey = useRef<{ sceneId: number | string | undefined; orientation: Orientation } | null>(null);
  useEffect(() => {
    if (sceneId === undefined) {
      fitKey.current = null;
      return;
    }
    const prev = fitKey.current;
    fitKey.current = { sceneId, orientation: previewOrientation };
    if (!prev || prev.sceneId !== sceneId) {
      if (!useSceneStore.getState().viewportRestored) fitToView();
      return;
    }
    if (prev.orientation !== previewOrientation) fitToView();
  }, [sceneId, previewOrientation, fitToView]);

  // Re-fit when the workspace (the canvas container) changes size
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let last: { w: number; h: number } | null = null;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[entries.length - 1]?.contentRect;
      if (!rect) return;
      const next = { w: Math.round(rect.width), h: Math.round(rect.height) };
      const changed = last !== null && (last.w !== next.w || last.h !== next.h);
      last = next;
      if (changed) fitToView();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [sceneId, fitToView]);

  // Middle-mouse drag panning
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panStartOffset = useRef({ x: 0, y: 0 });

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // Middle mouse button or Space+left click
    if (e.button === 1) {
      e.preventDefault();
      isPanning.current = true;
      panStart.current = { x: e.clientX, y: e.clientY };
      panStartOffset.current = { ...viewportPan };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }
  }, [viewportPan]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isPanning.current) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    setViewportPan({
      x: panStartOffset.current.x + dx,
      y: panStartOffset.current.y + dy,
    });
  }, [setViewportPan]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (isPanning.current) {
      isPanning.current = false;
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    }
  }, []);

  // Wheel handler: pinch/Ctrl+scroll = zoom, plain scroll = pan
  // Must be a native (non-passive) listener so preventDefault() actually blocks browser zoom
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      // Zoom around the pointer: the stage point under it stays under it
      const wrapper = wrapperRef.current;
      const newZoom = Math.max(0.1, Math.min(3, viewportZoom * Math.exp(-e.deltaY * 0.002)));
      if (!wrapper || newZoom === viewportZoom) {
        setViewportZoom(newZoom);
        return;
      }
      const rect = wrapper.getBoundingClientRect();
      // Pointer relative to the workspace centre (the viewport's untransformed centre)
      const px = e.clientX - (rect.left + rect.width / 2);
      const py = e.clientY - (rect.top + rect.height / 2);
      const k = newZoom / viewportZoom;
      setViewportZoom(newZoom);
      setViewportPan({
        x: px - k * (px - viewportPan.x),
        y: py - k * (py - viewportPan.y),
      });
    } else {
      // Pan
      setViewportPan({
        x: viewportPan.x - e.deltaX,
        y: viewportPan.y - e.deltaY,
      });
    }
  }, [viewportZoom, viewportPan, setViewportZoom, setViewportPan]);

  // Attach wheel as a non-passive native listener so preventDefault works for pinch-zoom
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Load images for sprites
  useEffect(() => {
    if (!scene) return;
    
    const mediaIds = new Set<number>();
    scene.sprites.forEach(sprite => {
      sprite.states.forEach(state => {
        if (state.mediaId && state.mediaId > 0) {
          mediaIds.add(state.mediaId);
        }
      });
    });

    // Each picture's spritesheet cut comes with it (spec 033 D-3)
    void useMediaSheetStore.getState().loadSheets(mediaIds);

    mediaIds.forEach(async (mediaId) => {
      if (spriteImages[mediaId]) return;
      try {
        const blob = await apiClient.getMediaFileBlob(mediaId);
        const url = URL.createObjectURL(blob);
        setSpriteImages(prev => ({ ...prev, [mediaId]: url }));
      } catch (e) {
        console.error('Failed to load media', mediaId, e);
      }
    });
  }, [scene, apiClient, spriteImages]);

  // Load the background's base images (spec 026: drawn cover-fit under the
  // layers): the one showing, and while a state change runs (spec 040) the
  // one it changes from. Loaded once each, kept by media id.
  const baseMediaId = frame?.background.baseMediaId;
  const fromMediaId = frame?.background.change?.fromMediaId;
  const [baseImages, setBaseImages] = useState<Record<number, string>>({});
  const requestedBase = useRef(new Set<number>());
  useEffect(() => {
    for (const id of [baseMediaId, fromMediaId]) {
      if (id === undefined || requestedBase.current.has(id)) continue;
      requestedBase.current.add(id);
      (async () => {
        try {
          const blob = await apiClient.getMediaFileBlob(id);
          const url = URL.createObjectURL(blob);
          setBaseImages((prev) => ({ ...prev, [id]: url }));
        } catch (e) {
          requestedBase.current.delete(id);
          console.error('Failed to load background image', id, e);
        }
      })();
    }
  }, [baseMediaId, fromMediaId, apiClient]);

  // Load images for background layers
  useEffect(() => {
    if (!scene?.background?.layers) return;
    
    scene.background.layers.forEach(async (layer) => {
      if (!layer.mediaId || backgroundImages[layer.id]) return;
      try {
        const blob = await apiClient.getMediaFileBlob(layer.mediaId);
        const url = URL.createObjectURL(blob);
        setBackgroundImages(prev => ({ ...prev, [layer.id]: url }));
      } catch (e) {
        console.error('Failed to load background layer', layer.id, e);
      }
    });
  }, [scene?.background?.layers, apiClient, backgroundImages]);

  // Get selected target element - use effect to get DOM element after render
  const [selectedTarget, setSelectedTarget] = useState<HTMLElement | null>(null);
  
  // Get selected sprite for key generation (forces Moveable remount on undo/redo)
  const selectedSprite = selection.ids.length === 1 && selection.type === 'sprite'
    ? scene?.sprites.find(s => s.id === selection.ids[0])
    : null;
  
  // Moveable key — only includes things that require a full remount.
  // Position, size, scale, and rotation are NOT included because they change
  // during Moveable interactions; including them causes key changes that
  // triggering the selectedTarget effect, making Moveable disappear on mouse-up.
  // Instead we call updateRect() after store changes for smooth updates.
  const moveableKey = selectedSprite 
    ? `${selectedSprite.id}-${previewOrientation}`
    : '';
  
  const moveableRef = useRef<Moveable>(null);

  // Acquire/release the DOM target when selection or Moveable key changes
  useEffect(() => {
    if (selection.ids.length !== 1 || selection.type !== 'sprite') {
      setSelectedTarget(null);
      return;
    }
    // Force null then re-set so Moveable fully remounts with fresh measurement
    // when mode/orientation changes. Double-rAF ensures browser has painted.
    setSelectedTarget(null);
    let cancelled = false;
    requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (cancelled) return;
        const el = document.querySelector(`[data-sprite-id="${selection.ids[0]}"]`) as HTMLElement | null;
        setSelectedTarget(el);
      });
    });
    return () => { cancelled = true; };
  }, [selection.ids, selection.type, moveableKey]);

  // Re-measure Moveable after it mounts on a target and after zoom, pan,
  // dimension or scene data changes (fit, undo/redo, scrubbing, gesture end).
  // Moveable shows its outline only from its second render, so the mount
  // needs this too, or a fresh selection has no visible outline.
  useEffect(() => {
    const timer = setTimeout(() => {
      moveableRef.current?.updateRect();
    }, 60);
    return () => clearTimeout(timer);
  }, [selectedTarget, viewportZoom, viewportPan, dimensions.width, dimensions.height, scene, playheadPosition]);

  // A click that ends a Moveable gesture is not a selection click
  const gestureEndedAt = useRef(0);

  // Clicking the empty stage or workspace selects the scene (Scene properties)
  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-sprite-id]')) return;
    if (target.closest('[data-dialog-id]')) return;
    if (target.closest('.moveable-control-box')) return;
    if (target.closest('[data-canvas-chrome]')) return;
    if (Date.now() - gestureEndedAt.current < 250) return;
    selectSingle('scene', 'scene');
  }, [selectSingle]);

  // Handle sprite click (select)
  const handleSpriteClick = useCallback((e: React.MouseEvent, spriteId: string) => {
    e.stopPropagation();
    selectSingle(spriteId, 'sprite');
  }, [selectSingle]);

  // Handle dialog click (select)
  const handleDialogClick = useCallback((e: React.MouseEvent, dialogId: string) => {
    e.stopPropagation();
    selectSingle(dialogId, 'dialog');
  }, [selectSingle]);

  // Handle drag end - update sprite position for current orientation
  // The position represents where the hinge point should be placed
  // Since we use CSS `translate(-hingeX%, -hingeY%)`, the CSS left/top IS where the hinge ends up
  // The hinge is clamped to −50…150 % of the stage (spec 005). In Timeline
  // mode an animated position is keyed at the playhead (spec 019); otherwise
  // the base layout of the active orientation is written.
  const handleDragEnd = useCallback((target: HTMLElement, spriteId: string, left: number, top: number) => {
    const clamped = clampSpritePosition({
      x: (left / dimensions.width) * 100,
      y: (top / dimensions.height) * 100,
    });
    // The DOM shows the clamped place even when the store value does not change
    target.style.left = `${(clamped.x / 100) * dimensions.width}px`;
    target.style.top = `${(clamped.y / 100) * dimensions.height}px`;
    commitSpriteTransform(spriteId, { position: clamped });
  }, [commitSpriteTransform, dimensions]);

  // Moveable gestures: the DOM follows live, the store gets one commit on
  // release, and the whole gesture is one undo step (018 history groups).
  const gestureFrame = useRef<GestureFrame | null>(null);
  const beginGesture = useCallback((target: HTMLElement) => {
    useSceneStore.getState().beginHistoryGroup();
    gestureFrame.current = {
      scale: parseFloat(target.dataset.scale ?? '1') || 1,
      rotation: parseFloat(target.dataset.rotation ?? '0') || 0,
      hingeX: parseFloat(target.dataset.hingeX ?? '0.5'),
      hingeY: parseFloat(target.dataset.hingeY ?? '0.5'),
    };
    return gestureFrame.current;
  }, []);
  const endGesture = useCallback(() => {
    gestureFrame.current = null;
    gestureEndedAt.current = Date.now();
    useSceneStore.getState().endHistoryGroup();
  }, []);
  // A gesture interrupted by an unmount still closes its history group
  useEffect(() => () => {
    if (gestureFrame.current) endGesture();
  }, [endGesture]);



  // One dialog layout cache per mounted canvas and measurer (spec 012): a line
  // is measured once, not on every playhead change.
  const dialogLayoutCache = useMemo(() => createDialogLayoutCache(), [measureDialogText]);

  if (!scene || !frame) {
    return (
      <div className={styles.emptyCanvas}>
        <p>No scene selected</p>
      </div>
    );
  }

  // The choice panel (spec 008): at or after the reveal (on the player's
  // length), or while a ChoiceEditor input has focus. During ▶ Preview the
  // player draws its own panel over the canvas (spec 009).
  const choices = scene.choices ?? [];
  const revealMs = computeRevealTime(previewDurationMs(scene, authoredKeyframes), scene.choiceSettings?.showBeforeEnd, scene.exitTransition);
  const showChoicePanel = choices.length > 0 && !isPlaying
    && (playheadPosition >= revealMs || choiceEditorFocused);
  // Each dialog box drawn on the canvas, laid out as the player lays it out
  // (012's layoutDialogBox, spec 017): sized from the line's whole text, or
  // while no line shows from the neighbouring line; a dialog off screen at the
  // playhead keeps the empty box as its placeholder.
  const measure = measureDialogText ?? canvasDialogMeasurer();
  const dialogLayouts = new Map<string, DialogBoxLayout>();
  for (const dialog of scene.dialogs) {
    const fd = frame.dialogs.find((d) => d.id === dialog.id);
    if (isPlaying && !fd) continue;
    const text = fd ? dialogBoxText(dialog, fd.lineId, frame.t) : '';
    dialogLayouts.set(dialog.id, dialogLayoutCache.layout(text, dialog.position || 'bottom', dimensions, measure));
  }
  // The panel stacks 10 px above the highest bottom dialog box (020's rule)
  const dialogBoxes: DialogBoxRect[] = scene.dialogs.flatMap((d) => {
    const box = dialogLayouts.get(d.id);
    return box ? [{ id: d.id, position: d.position || 'bottom', x: box.x, y: box.y, width: box.width, height: box.height }] : [];
  });
  const choicePanelBottomPx = choicePanelBottom(dialogBoxes, dimensions.height);
  const handleChoiceOptionClick = (choice: ChoiceOption) => {
    focusChoiceInput(choice.id);
  };

  // A background state change (spec 039 D-4, 040): drawFrame's drawBase in DOM.
  const baseChange = frame.background.change;
  const changeProgress = baseChange ? Math.min(1, Math.max(0, baseChange.progress)) : 1;
  const showFromBase = !!baseChange && changeProgress < 1;
  const showToBase = !baseChange || changeProgress > 0;
  const toBaseStyle = baseChange && changeProgress > 0 && changeProgress < 1
    ? backgroundChangeStyle(baseChange.type, baseChange.direction, changeProgress)
    : undefined;

  // Count background effects
  const bgEffects = {
    layers: scene.background?.layers?.length || 0,
    translate: scene.background?.layers?.some(l => l.animation?.translate?.enabled) || false,
    zoom: scene.background?.layers?.some(l => l.animation?.zoom?.enabled) || false,
    rotate: scene.background?.layers?.some(l => l.animation?.rotate?.enabled) || false,
  };

  return (
    <div
      ref={wrapperRef}
      className={styles.canvasWrapper}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={handleCanvasClick}
      data-testid="canvas-workspace"
      style={isPlaying ? { pointerEvents: 'none', userSelect: 'none' } : undefined}
    >
      <div
        className={styles.viewport}
        style={{
          transform: `translate(${viewportPan.x}px, ${viewportPan.y}px) scale(${viewportZoom})`,
        }}
      >
        <div
          ref={containerRef}
          className={styles.canvas}
          data-testid="canvas-stage"
          style={{ width: dimensions.width, height: dimensions.height }}
        >
        {/* Dims everything drawn outside the stage: what the learner will not see */}
        <div className={styles.stageMask} aria-hidden="true" />
        {/* Background color */}
        <div 
          className={styles.background}
          style={{ backgroundColor: frame.background.color }}
        />

        {/* A background state change (spec 040): the "from" base under the "to" one, as drawFrame's drawBase */}
        {showFromBase && fromMediaId !== undefined && baseImages[fromMediaId] && (
          <div className={styles.backgroundLayer} style={{ zIndex: 0 }}>
            <img
              src={baseImages[fromMediaId]}
              alt="Background"
              className={styles.backgroundLayerImage}
              draggable={false}
              data-media-id={fromMediaId}
              data-testid="canvas-background-from"
              onLoad={(e) => noteImageSize(fromMediaId, e.currentTarget)}
            />
          </div>
        )}
        {showFromBase && frame.background.change?.fromPlaceholder !== undefined && (
          <div className={styles.backgroundPlaceholder} data-testid="canvas-background-from-placeholder" aria-hidden="true">
            <span>{frame.background.change.fromPlaceholder.toUpperCase()}</span>
          </div>
        )}

        {/* The base image, cover-fit over the colour and under the layers (spec 026) */}
        {showToBase && baseMediaId !== undefined && baseImages[baseMediaId] && (
          <div className={styles.backgroundLayer} style={{ zIndex: 0, ...toBaseStyle }} data-testid="canvas-background-to">
            <img
              src={baseImages[baseMediaId]}
              alt="Background"
              className={styles.backgroundLayerImage}
              draggable={false}
              data-media-id={baseMediaId}
              data-testid="canvas-background-base"
              onLoad={(e) => noteImageSize(baseMediaId, e.currentTarget)}
            />
          </div>
        )}
        {/* A named background without a picture: its labelled placeholder (spec 026 D-11) */}
        {showToBase && frame.background.placeholder !== undefined && (
          <div className={styles.backgroundPlaceholder} style={toBaseStyle} data-testid="canvas-background-placeholder" aria-hidden="true">
            <span>{frame.background.placeholder.toUpperCase()}</span>
          </div>
        )}

        {/* Background layers (static display; their opacity from the frame) */}
        {scene.background?.layers?.map((layer) => {
          const imgUrl = backgroundImages[layer.id];
          if (!imgUrl) return null;
          const frameLayer = frame.background.layers.find((l) => l.id === layer.id);
          
          return (
            <div
              key={layer.id}
              className={styles.backgroundLayer}
              style={{
                opacity: frameLayer?.opacity ?? layer.opacity,
                zIndex: (layer as any).zIndex ?? 0,
              }}
            >
              <img 
                src={imgUrl} 
                alt={`Layer ${layer.id}`}
                className={styles.backgroundLayerImage}
                draggable={false}
                data-media-id={layer.mediaId}
                onLoad={(e) => noteImageSize(layer.mediaId, e.currentTarget)}
              />
            </div>
          );
        })}

        {/* Background info overlay with hover popover */}
        {bgEffects.layers > 0 && (
          <div className={styles.backgroundInfo}>
            <span className={styles.bgInfoIcon}>
              🖼️ {bgEffects.layers}
            </span>
            {bgEffects.translate && (
              <span className={styles.bgInfoIcon}>↔️</span>
            )}
            {bgEffects.zoom && (
              <span className={styles.bgInfoIcon}>🔍</span>
            )}
            {bgEffects.rotate && (
              <span className={styles.bgInfoIcon}>🔄</span>
            )}
            
            {/* Hover popover */}
            <div className={styles.bgInfoPopover}>
              <div className={styles.popoverHeader}>Background Effects</div>
              <div className={styles.popoverContent}>
                <div className={styles.popoverSection}>
                  <div className={styles.popoverLabel}>Layers</div>
                  <div className={styles.popoverValue}>{bgEffects.layers}</div>
                </div>
                
                {scene.background?.layers?.map((layer, index) => {
                  const anim = layer.animation;
                  const hasEffects = anim?.translate?.enabled || anim?.zoom?.enabled || anim?.rotate?.enabled;
                  
                  return (
                    <div key={layer.id} className={styles.popoverLayer}>
                      <div className={styles.popoverLayerHeader}>
                        Layer {index + 1}
                        <span className={styles.popoverOpacity}>{Math.round(layer.opacity * 100)}%</span>
                      </div>
                      
                      {!hasEffects && (
                        <div className={styles.popoverNoEffects}>No animations</div>
                      )}
                      
                      {anim?.translate?.enabled && (
                        <div className={styles.popoverEffect}>
                          <span className={styles.effectIcon}>↔️</span>
                          <span className={styles.effectName}>Translate</span>
                          <span className={styles.effectDetails}>
                            {anim.translate.direction} · {anim.translate.speed}px/s
                            {anim.translate.loop && ' · loop'}
                          </span>
                        </div>
                      )}
                      
                      {anim?.zoom?.enabled && (
                        <div className={styles.popoverEffect}>
                          <span className={styles.effectIcon}>🔍</span>
                          <span className={styles.effectName}>Zoom</span>
                          <span className={styles.effectDetails}>
                            {Math.round(anim.zoom.scale * 100)}% · {(anim.zoom.duration / 1000).toFixed(1)}s
                            {anim.zoom.yoyo && ' · yoyo'}
                            {anim.zoom.loop && ' · loop'}
                          </span>
                        </div>
                      )}
                      
                      {anim?.rotate?.enabled && (
                        <div className={styles.popoverEffect}>
                          <span className={styles.effectIcon}>🔄</span>
                          <span className={styles.effectName}>Rotate</span>
                          <span className={styles.effectDetails}>
                            {anim.rotate.angle}° · {(anim.rotate.duration / 1000).toFixed(1)}s
                            {anim.rotate.yoyo && ' · yoyo'}
                            {anim.rotate.loop && ' · loop'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Sprites: where the frame puts them at the playhead (spec 017) */}
        {scene.sprites.map((sprite) => {
          const f: FrameSprite | undefined = frame.sprites.find((fs) => fs.id === sprite.id);
          if (!f) return null;
          const isSelected = selection.ids.includes(sprite.id) && selection.type === 'sprite';
          const size = { width: f.width, height: f.height };
          const xPercent = f.x;
          const yPercent = f.y;
          const { scale, rotation, opacity } = f;
          // D-3: invisible at the playhead (e.g. before its entrance) is drawn
          // as a selectable ghost; the reported FrameState keeps opacity 0.
          const hiddenAtPlayhead = !(opacity > 0);
          const stateName = f.state;
          const stateImage = f.mediaId ? spriteImages[f.mediaId] : null;
          const hinge = { x: f.hingeX, y: f.hingeY };
          const flip = flipCss(f.flipX, f.flipY);
          
          // The element's CSS left/top is where the hinge sits (stage px);
          // the transform offsets the sprite around it.
          const hingePixelX = (xPercent / 100) * dimensions.width;
          const hingePixelY = (yPercent / 100) * dimensions.height;
          
          // Wholly outside the stage: drawn as a dimmed outline in the workspace
          const offCanvas = isPoseOffCanvas(
            { x: xPercent, y: yPercent, scale, rotation, hinge, size },
            dimensions,
          );

          return (
            <div
              key={sprite.id}
              data-sprite-id={sprite.id}
              data-off-canvas={offCanvas ? 'true' : undefined}
              data-hidden-at-playhead={hiddenAtPlayhead ? 'true' : undefined}
              data-scale={scale}
              data-rotation={rotation}
              data-hinge-x={hinge.x}
              data-hinge-y={hinge.y}
              data-flip-x={f.flipX ? 'true' : undefined}
              data-flip-y={f.flipY ? 'true' : undefined}
              className={`${styles.sprite} ${isSelected ? styles.selected : ''} ${offCanvas ? styles.offCanvas : ''}`}
              style={{
                position: 'absolute',
                left: hingePixelX,
                top: hingePixelY,
                width: size.width,
                height: size.height,
                transform: spriteTransformCss({ scale, rotation, hingeX: hinge.x, hingeY: hinge.y }),
                transformOrigin: `${hinge.x * 100}% ${hinge.y * 100}%`,
                opacity: hiddenAtPlayhead ? GHOST_OPACITY : opacity,
                ...(hiddenAtPlayhead ? { outline: GHOST_OUTLINE, outlineOffset: 0 } : {}),
                zIndex: f.zIndex,
              }}
              onClick={(e) => handleSpriteClick(e, sprite.id)}
            >
              {stateImage && f.sheet && f.mediaId && playable?.sheets?.[f.mediaId] ? (
                <SheetFrameImage
                  src={stateImage}
                  alt={sprite.name}
                  mediaId={f.mediaId}
                  cut={playable.sheets[f.mediaId]}
                  index={f.sheet.index}
                  pixelArt={f.sheet.pixelArt}
                  box={size}
                  natural={imageSizes[f.mediaId]}
                  flip={flip}
                  onLoad={(img) => { if (f.mediaId) noteImageSize(f.mediaId, img); }}
                />
              ) : stateImage ? (
                <img
                  src={stateImage}
                  alt={sprite.name}
                  className={styles.spriteImage}
                  draggable={false}
                  data-media-id={f.mediaId ?? undefined}
                  style={flip ? { transform: flip } : undefined}
                  onLoad={(e) => { if (f.mediaId) noteImageSize(f.mediaId, e.currentTarget); }}
                />
              ) : f.placeholder !== undefined ? (
                // No picture to show: spec 026's labelled placeholder, as the player draws it
                <div className={styles.picturePlaceholder} data-testid="sprite-placeholder">
                  <span>{f.placeholder.toUpperCase()}</span>
                </div>
              ) : (
                <div className={styles.spritePlaceholder}>
                  <span>{sprite.name}</span>
                  <small>{stateName}</small>
                </div>
              )}
            </div>
          );
        })}

        {/* Dialogs - positioned at top or bottom, non-transformable. The panel
            and its visible text at the playhead come from the frame (spec 017);
            a dialog not on screen at the playhead stays as a clickable
            placeholder while authoring. */}
        {scene.dialogs.map((dialog) => {
          const isSelected = selection.ids.includes(dialog.id) && selection.type === 'dialog';
          const fd: FrameDialog | undefined = frame.dialogs.find((d) => d.id === dialog.id);
          const box = dialogLayouts.get(dialog.id);
          if (!box) return null;
          const shownLines = fd?.lineId ? visibleDialogLines(box.lines, fd.text, box.cut) : [];
          const activeLine = fd?.lineId ? dialog.lines?.find((l) => l.id === fd.lineId) : undefined;
          
          // Get styling from line or dialog defaults
          const textColor = activeLine?.textColor || dialog.textColor || '#ffffff';
          const outlineColor = activeLine?.outlineColor || dialog.outlineColor;
          const outlineWidth = activeLine?.outlineWidth ?? dialog.outlineWidth ?? 2;
          
          // Build text stroke style for outline
          const textStrokeStyle = outlineColor ? {
            WebkitTextStroke: `${outlineWidth}px ${outlineColor}`,
            paintOrder: 'stroke fill' as const,
          } : {};
          
          return (
            <div
              key={dialog.id}
              data-dialog-id={dialog.id}
              data-testid="canvas-dialog"
              className={`${styles.dialogBox} ${isSelected ? styles.dialogSelected : ''}`}
              style={{
                left: box.x,
                top: box.y,
                width: box.width,
                height: box.height,
                opacity: fd ? fd.panelOpacity : undefined,
              }}
              onClick={(e) => handleDialogClick(e, dialog.id)}
            >
              {fd && fd.lineId ? (
                <div
                  className={styles.dialogTextPreview}
                  data-testid="canvas-dialog-text"
                  style={{
                    color: textColor,
                    ...textStrokeStyle,
                    font: DIALOG_FONT,
                    opacity: fd.panelOpacity > 0 ? fd.textOpacity / fd.panelOpacity : 0,
                  }}
                >
                  {/* One element per laid-out line, so the typed prefix keeps the final breaks */}
                  {box.lines.map((_, i) => (
                    <div
                      key={i}
                      className={styles.dialogLine}
                      data-testid="canvas-dialog-line"
                      style={{ lineHeight: `${box.lineHeight}px`, height: box.lineHeight }}
                    >
                      {shownLines[i] ?? ''}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <span className={styles.dialogIcon}>💬</span>
                  <span className={styles.dialogName}>{dialog.name || 'Dialog'}</span>
                  {(dialog.lines?.length || 0) > 0 && (
                    <span className={styles.dialogCount}>({dialog.lines.length})</span>
                  )}
                </>
              )}
            </div>
          );
        })}

        {/* Moveable for selected sprite - hidden during preview */}
        {/* Choice panel: where and when the learner sees it (spec 008) */}
        {showChoicePanel && (
          <CanvasChoicePanel
            choices={choices}
            settings={scene.choiceSettings}
            bottomPx={choicePanelBottomPx}
            onOptionClick={handleChoiceOptionClick}
          />
        )}
      </div>
        {/* Moveable for selected sprite - rendered outside canvas to avoid overflow:hidden clipping */}
        {selectedTarget && !isPlaying && (
          <Moveable
            ref={moveableRef}
            key={moveableKey}
            target={selectedTarget}
            container={containerRef.current}
            rootContainer={wrapperRef.current || undefined}
            zoom={1 / viewportZoom}
            draggable={true}
            scalable={true}
            rotatable={true}
            keepRatio={true}
            origin={false}
            onDragStart={({ target }) => { beginGesture(target as HTMLElement); }}
            onDrag={({ target, left, top }) => {
              target.style.left = `${left}px`;
              target.style.top = `${top}px`;
            }}
            onDragEnd={({ target, isDrag }) => {
              // A click without movement is not an edit (spec 018)
              const spriteId = target.getAttribute('data-sprite-id');
              if (isDrag && spriteId) {
                const el = target as HTMLElement;
                handleDragEnd(el, spriteId, parseFloat(el.style.left) || 0, parseFloat(el.style.top) || 0);
              }
              endGesture();
            }}
            onScaleStart={(e) => {
              const frame = beginGesture(e.target as HTMLElement);
              e.set([frame.scale, frame.scale]);
            }}
            onScale={({ target, scale }) => {
              const frame = gestureFrame.current;
              if (!frame) return;
              frame.scale = Math.max(0.01, scale[0]);
              target.style.transform = spriteTransformCss(frame);
            }}
            onScaleEnd={({ target, isDrag }) => {
              const spriteId = target.getAttribute('data-sprite-id');
              const frame = gestureFrame.current;
              if (isDrag && spriteId && frame) {
                const scale = Math.round(frame.scale * 1000) / 1000;
                commitSpriteTransform(spriteId, { scale });
              }
              endGesture();
            }}
            onRotateStart={(e) => {
              const frame = beginGesture(e.target as HTMLElement);
              e.set(frame.rotation);
            }}
            onRotate={({ target, rotate }) => {
              const frame = gestureFrame.current;
              if (!frame) return;
              frame.rotation = rotate;
              target.style.transform = spriteTransformCss(frame);
            }}
            onRotateEnd={({ target, isDrag }) => {
              const spriteId = target.getAttribute('data-sprite-id');
              const frame = gestureFrame.current;
              if (isDrag && spriteId && frame) {
                const rotation = Math.round(frame.rotation * 10) / 10;
                commitSpriteTransform(spriteId, { rotation });
              }
              endGesture();
            }}
          />
        )}
      </div>
      {/* Zoom indicator */}
      <div className={styles.zoomIndicator} data-canvas-chrome>
        <button className={styles.zoomButton} onClick={fitToView} title="Fit to view" aria-label="Fit to view">⊡</button>
        <span className={styles.zoomLevel} data-testid="zoom-level">{Math.round(viewportZoom * 100)}%</span>
      </div>
    </div>
  );
};

export default SceneCanvas;
